import {DateTime} from "luxon";
import type React from "react";
import {fetchData} from "~/components/reports/widgetComponent";
import {createDefaultTableComparisonConfig} from "~/domainModels/reports/tableComparison";
import {WidgetLoadingState, type Widget} from "~/components/reports/widgetSpecific/widgets";
import {
    DateRangeOptions,
    QueryResolverErrorCodes,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_reportScaffold/report/edit/$reportSlug/-types";
import {
    getComparisonDateRangeForPrimaryRange,
    getDateRangeForCanvasControl,
    getDatesForDateRangeOption,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_reportScaffold/report/edit/$reportSlug/-utilities";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {errorResult, type Result} from "@vesta/error-handling-remix/result";
import {getEnumValueFromUnknown} from "@vesta/global-common-typescript/utilities/typeValidationUtilities";
import type {CanvasControl, CanvasControls} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import {
    CanvasControlType,
    WidgetType,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";

// TODO: Move types to sensible location
export type ReportPage = {
    id: Uuid;
    title: string;
    widgets: Array<Widget>;
    startDateIso: Iso8601DateTime | null;
    startDateJs: Date | null;
    endDateIso: Iso8601DateTime | null;
    endDateJs: Date | null;
    dateRange: DateRangeOptions;
};

export type ReportState = {
    pages: Array<ReportPage>;
    canvasControls: CanvasControls;
    selectedWidget: Widget | null;
    selectedPopupType: ReportViewPopups | null;
    timezone: string;
};

function resetTableComparisonData(widget: Extract<Widget, {type: WidgetType.table}>): void {
    widget.comparisonData = null;
    widget.comparisonColumnMetadata = null;
    widget.comparisonColumnAttributeIds = null;
    widget.comparisonLoadingState = WidgetLoadingState.notStarted;
    widget.comparisonErrorCode = null;
}

export enum ReportViewPopups {
    filters = "46a29fc0-1bb5-4c1c-bcb3-af9d87e69694",
}

export enum ReportActionType {
    // changePageDates,
    // editPageDateRange,
    editWidget,
    editSelectedWidget,
    editCanvasControl,
    setSelectedWidget,
    setSelectedPopupType,
    setCanvasControlValue,
}

export type ReportAction =
    // | {
    //       actionType: ReportActionType.changePageDates;
    //       startDate: Date | null;
    //       endDate: Date | null;
    //       pageIndex: number;
    //   }
    | {
          actionType: ReportActionType.editWidget;
          widget: Widget;
          changedFields: Partial<Widget>;
      }
    | {
          actionType: ReportActionType.editSelectedWidget;
          widget: Widget;
          changedFields: Partial<Widget>;
      }
    // | {
    //       actionType: ReportActionType.editPageDateRange;
    //       dateRange: DateRangeOptions;
    //       pageIndex: number;
    //   }
    | {
          actionType: ReportActionType.editCanvasControl;
          canvasControl: CanvasControl;
          changedFields: Omit<Partial<CanvasControl>, "id">;
      }
    | {
          actionType: ReportActionType.setSelectedWidget;
          widget: Widget;
      }
    | {
          actionType: ReportActionType.setSelectedPopupType;
          popup: ReportViewPopups;
      }
    | {
          actionType: ReportActionType.setCanvasControlValue;
          canvasControl: CanvasControl;
          value: string;
      };

export function reportReducer(state: ReportState, action: ReportAction): ReportState {
    const newState = structuredClone(state);
    switch (action.actionType) {
        case ReportActionType.editWidget: {
            pageLoop: for (const page of newState.pages) {
                for (const widget of page.widgets) {
                    if (action.widget.id == widget.id) {
                        Object.assign(widget, action.changedFields);
                        const primaryDateChanged =
                            action.changedFields.startDateIso !== undefined ||
                            action.changedFields.endDateIso !== undefined;

                        if (widget.type === WidgetType.table && (primaryDateChanged || action.changedFields.comparison !== undefined)) {
                            resetTableComparisonData(widget);
                        }

                        if (
                            widget.type === WidgetType.table &&
                            action.changedFields.loadingState === WidgetLoadingState.loading &&
                            widget.comparison.enabled === true &&
                            widget.comparison.startDateIso !== null &&
                            widget.comparison.endDateIso !== null
                        ) {
                            widget.comparisonLoadingState = WidgetLoadingState.loading;
                        }

                        if (
                            action.changedFields.startDateIso != null ||
                            action.changedFields.endDateIso != null ||
                            action.changedFields.startDateJs != null ||
                            action.changedFields.endDateJs != null
                        ) {
                            widget.dateRange = null;
                        }

                        if (action.changedFields.dateRange != null) {
                            const staticDateRangeResult = getEnumValueFromUnknown(action.changedFields.dateRange, DateRangeOptions);

                            const staticDateRange = staticDateRangeResult.success === false ? null : staticDateRangeResult.data;

                            if (staticDateRange == null) {
                                const canvasControl = newState.canvasControls[action.changedFields.dateRange];

                                if (canvasControl == null) {
                                    break pageLoop;
                                }

                                const startDate = canvasControl.value[0];
                                const endDate = canvasControl.value[1];

                                if (startDate == null || endDate == null) {
                                    break pageLoop;
                                }

                                widget.startDateIso = startDate;
                                widget.endDateIso = endDate;
                                widget.startDateJs = DateTime.fromISO(startDate, {setZone: true}).toJSDate();
                                widget.endDateJs = DateTime.fromISO(endDate, {
                                    setZone: true,
                                }).toJSDate();
                                widget.dateRangeCanvasControl = action.changedFields.dateRange;
                            } else {
                                const {startDate, endDate} = getDatesForDateRangeOption(action.changedFields.dateRange, newState.timezone);

                                widget.startDateIso = startDate;
                                widget.endDateIso = endDate;
                                widget.startDateJs = DateTime.fromISO(startDate).toJSDate();
                                widget.endDateJs = DateTime.fromISO(endDate).toJSDate();
                                widget.dateRangeCanvasControl = null;
                            }
                        }

                        break pageLoop;
                    }
                }
            }
            break;
        }
        case ReportActionType.editSelectedWidget: {
            Object.assign(newState.selectedWidget, action.changedFields);

            pageLoop: for (const page of newState.pages) {
                for (const widget of page.widgets) {
                    if (newState.selectedWidget.id == widget.id) {
                        Object.assign(widget, newState.selectedWidget);
                        break pageLoop;
                    }
                }
            }

            break;
        }
        // case ReportActionType.editPageDateRange: {
        //     const pageIndex = action.pageIndex;
        //     const dateRange = action.dateRange;

        //     const page = newState.pages[pageIndex];
        //     if (page == null) {
        //         break;
        //     }

        //     const {startDate, endDate} = getDatesForDateRangeOption(
        //         dateRange,
        //         newState.timezone,
        //     );

        //     page.startDateIso = startDate;
        //     page.endDateIso = endDate;
        //     page.startDateJs = DateTime.fromISO(startDate).toJSDate();
        //     page.endDateJs = DateTime.fromISO(endDate).toJSDate();

        //     page.dateRange = dateRange;

        //     break;
        // }
        case ReportActionType.editCanvasControl: {
            const canvasControl = structuredClone(action.canvasControl);

            Object.assign(canvasControl, action.changedFields);

            newState.canvasControls[canvasControl.id] = canvasControl;

            const dateRange = getDateRangeForCanvasControl(canvasControl, newState.timezone, {useDefaultValue: false});
            if (dateRange !== null) {
                for (const page of newState.pages) {
                    for (const widget of page.widgets) {
                        if (widget.type !== WidgetType.table || widget.dateRangeCanvasControl !== canvasControl.id) {
                            continue;
                        }

                        widget.comparison = {
                            ...widget.comparison,
                            ...getComparisonDateRangeForPrimaryRange(
                                dateRange.startDateIso,
                                dateRange.endDateIso,
                                newState.timezone,
                            ),
                        };
                        resetTableComparisonData(widget);
                    }
                }
            }

            break;
        }
        case ReportActionType.setSelectedWidget: {
            const widget = action.widget;

            newState.selectedWidget = widget;
            break;
        }
        case ReportActionType.setSelectedPopupType: {
            const popup = action.popup;

            newState.selectedPopupType = popup;
            break;
        }
        case ReportActionType.setCanvasControlValue: {
            const canvasControl = action.canvasControl;
            const value = action.value;

            newState.canvasControls[canvasControl.id].value = value;

            break;
        }
        default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const exhaustiveCheck: never = action;
            throw new Error(`Encountered unexpected action: ${action}`);
        }
    }

    for (const page of newState.pages) {
        const previousPage = state.pages.find((statePage) => statePage.id === page.id);
        if (previousPage === undefined) {
            continue;
        }

        for (const widget of page.widgets) {
            if (widget.type !== WidgetType.table) {
                continue;
            }

            const previousWidget = previousPage.widgets.find((stateWidget) => stateWidget.id === widget.id);
            if (previousWidget === undefined || previousWidget.type !== WidgetType.table) {
                continue;
            }

            if (previousWidget.startDateIso === widget.startDateIso && previousWidget.endDateIso === widget.endDateIso) {
                continue;
            }

            widget.comparison = {
                ...widget.comparison,
                ...getComparisonDateRangeForPrimaryRange(widget.startDateIso, widget.endDateIso, newState.timezone),
            };
            resetTableComparisonData(widget);
        }
    }

    return newState;
}

export function generateReportInitialState({
    pages,
    canvasControls,
    timezone,
    defaultDateRange,
}: {
    pages: Array<ReportPage>;
    canvasControls: CanvasControls;
    timezone: string;
    defaultDateRange?: DateRangeOptions | null;
}): ReportState {
    // TODO: Remove this default check
    const canvasControlsCloned = canvasControls == null ? {} : structuredClone(canvasControls);
    for (const canvasControl of Object.values(canvasControlsCloned)) {
        canvasControl.value = canvasControl.defaultValue;
        if (canvasControl.type === CanvasControlType.dateRange) {
            if (defaultDateRange !== undefined && defaultDateRange !== null) {
                const {startDate, endDate} = getDatesForDateRangeOption(defaultDateRange, timezone);

                canvasControl.dateRange = defaultDateRange;
                canvasControl.value = [startDate, endDate];
                canvasControl.defaultValue = [startDate, endDate];
                continue;
            }
            if (canvasControl.defaultDateRange != null) {
                const {startDate, endDate} = getDatesForDateRangeOption(canvasControl.defaultDateRange, timezone);

                canvasControl.dateRange = canvasControl.defaultDateRange;
                canvasControl.value = [startDate, endDate];
                canvasControl.defaultValue = [startDate, endDate];
            }
        }
    }

    const clonedPages = structuredClone(pages).map((page) => {
        return {
            ...page,
            widgets: page.widgets.map((widget) => {
                const canvasDateRange =
                    widget.dateRangeCanvasControl === null || widget.dateRangeCanvasControl === undefined
                        ? null
                        : getDateRangeForCanvasControl(canvasControlsCloned[widget.dateRangeCanvasControl], timezone);

                let resolvedWidget = widget;
                if (canvasDateRange !== null) {
                    resolvedWidget = {
                        ...widget,
                        startDateIso: canvasDateRange.startDateIso,
                        endDateIso: canvasDateRange.endDateIso,
                        startDateJs: DateTime.fromISO(canvasDateRange.startDateIso, {setZone: true}).toJSDate(),
                        endDateJs: DateTime.fromISO(canvasDateRange.endDateIso, {setZone: true}).toJSDate(),
                    };
                } else if (widget.dateRange != null) {
                    const {startDate, endDate} = getDatesForDateRangeOption(widget.dateRange, timezone);
                    resolvedWidget = {
                        ...widget,
                        startDateIso: startDate,
                        endDateIso: endDate,
                        startDateJs: DateTime.fromISO(startDate).toJSDate(),
                        endDateJs: DateTime.fromISO(endDate).toJSDate(),
                    };
                } else if (widget.startDateIso != null && widget.endDateIso != null) {
                    resolvedWidget = {
                        ...widget,
                        startDateJs: DateTime.fromISO(widget.startDateIso, {setZone: true}).toJSDate(),
                        endDateJs: DateTime.fromISO(widget.endDateIso, {setZone: true}).toJSDate(),
                    };
                } else {
                    const startDateIso = DateTime.now().minus({month: 1}).toISO() as Iso8601DateTime;
                    const endDateIso = DateTime.now().toISO() as Iso8601DateTime;

                    resolvedWidget = {
                        ...widget,
                        startDateIso,
                        endDateIso,
                        startDateJs: DateTime.fromISO(startDateIso).toJSDate(),
                        endDateJs: DateTime.fromISO(endDateIso).toJSDate(),
                    };
                }

                if (resolvedWidget.type !== WidgetType.table) {
                    return resolvedWidget;
                }

                const comparison = resolvedWidget.comparison ?? createDefaultTableComparisonConfig();

                return {
                    ...resolvedWidget,
                    comparison: {
                        ...comparison,
                        ...(resolvedWidget.dateRange != null || resolvedWidget.dateRangeCanvasControl != null
                            ? getComparisonDateRangeForPrimaryRange(resolvedWidget.startDateIso, resolvedWidget.endDateIso, timezone)
                            : {}),
                    },
                };
            }),
        };
    });

    return {
        pages: clonedPages,
        canvasControls: canvasControlsCloned,
        selectedPopupType: null,
        selectedWidget: null,
        timezone: timezone,
    };
}

// export function getDataFromWidgetDataState(
//     widgetDataState: WidgetDataState,
//     widgetDataDispatch: React.Dispatch<WidgetDataAction>,
//     widgetId: Uuid,
// ): Result<Partial<Widget>, QueryResolverErrorCodes> | undefined {
//     return widgetDataState.finishedRequests.get(widgetId);
// }

export type WidgetDataState = {
    queuedRequests: Set<Uuid>;
    ongoingRequests: Map<
        Uuid,
        {
            abortController: AbortController;
            timeoutId: NodeJS.Timeout;
        }
    >;
    /**
     * Undefined when request is still in progress, dataResult<Partial<Widget>> when data has been successfully
     * fetched, errorResult<QueryResolverErrorCodes> when there was an error loading the data
     */
    finishedRequests: Map<Uuid, Result<Partial<Widget>, QueryResolverErrorCodes>>;
};

export type WidgetDataAction =
    | {
          type: "loadDataForWidgetIfRequired";
          widget: Widget;
          reportState: ReportState;
          // reportDispatch: React.Dispatch<ReportAction>;
          widgetDataDispatch: React.Dispatch<WidgetDataAction>;
          companyId: Uuid;
          accessToken: string;
          intellsysBaseUrl: string;
      }
    | {
          type: "internal_markDataLoadingComplete";
          widget: Widget;
          reportState: ReportState;
          // reportDispatch: React.Dispatch<ReportAction>;
          widgetDataDispatch: React.Dispatch<WidgetDataAction>;
          widgetDataResult: Result<Partial<Widget>, QueryResolverErrorCodes>;
          requestAbortController: AbortController;
      }
    | {
          type: "invalidateDataForWidget";
          widgetId: Uuid;
      }
    | {
          type: "invalidateDataForAllWidgets";
      }
    | {
          type: "cancelAllRequests";
      };

const requestTimeout = 60000;

export function widgetDataReducer(state: WidgetDataState, action: WidgetDataAction): WidgetDataState {
    switch (action.type) {
        case "loadDataForWidgetIfRequired": {
            // console.log("widgetDataReducer: loadDataForWidgetIfRequired:", action.widget.id);
            if (
                state.queuedRequests.has(action.widget.id) === true ||
                state.ongoingRequests.has(action.widget.id) === true ||
                state.finishedRequests.has(action.widget.id) === true
            ) {
                return state;
            }

            // action.reportDispatch({
            //     actionType: ReportActionType.editWidget,
            //     widget: action.widget,
            //     changedFields: {
            //         loadingState: WidgetLoadingState.loading,
            //     },
            // });

            const abortController = new AbortController();
            const timeoutId = setTimeout(() => abortController.abort(), requestTimeout);

            fetchData(action.widget, action.reportState, action.companyId, action.accessToken, action.intellsysBaseUrl, abortController.signal)
                .then((widgetDataResult) => {
                    clearTimeout(timeoutId);
                    action.widgetDataDispatch({
                        type: "internal_markDataLoadingComplete",
                        widget: action.widget,
                        reportState: action.reportState,
                        // reportDispatch: action.reportDispatch,
                        widgetDataDispatch: action.widgetDataDispatch,
                        widgetDataResult: widgetDataResult,
                        requestAbortController: abortController,
                    });
                })
                .catch((exception) => {
                    action.widgetDataDispatch({
                        type: "internal_markDataLoadingComplete",
                        widget: action.widget,
                        reportState: action.reportState,
                        // reportDispatch: action.reportDispatch,
                        widgetDataDispatch: action.widgetDataDispatch,
                        widgetDataResult: errorResult(QueryResolverErrorCodes.hadesError),
                        requestAbortController: abortController,
                    });
                    // action.reportDispatch({
                    //     actionType: ReportActionType.editWidget,
                    //     widget: action.widget,
                    //     changedFields: {
                    //         loadingState: WidgetLoadingState.errorAfterLoading,
                    //         errorCode: QueryResolverErrorCodes.hadesError,
                    //     },
                    // });
                });

            const newState: WidgetDataState = {
                queuedRequests: state.queuedRequests,
                ongoingRequests: new Map(state.ongoingRequests),
                finishedRequests: state.finishedRequests,
            };
            newState.ongoingRequests.set(action.widget.id, {
                abortController: abortController,
                timeoutId: timeoutId,
            });
            return newState;
        }
        case "internal_markDataLoadingComplete": {
            // console.log("widgetDataReducer: internal_markDataLoadingComplete:", action.widget.id);
            const ongoingRequest = state.ongoingRequests.get(action.widget.id);
            if (ongoingRequest === undefined || ongoingRequest.abortController !== action.requestAbortController) {
                return state;
            }

            const newState: WidgetDataState = {
                queuedRequests: state.queuedRequests,
                ongoingRequests: new Map(state.ongoingRequests),
                finishedRequests: new Map(state.finishedRequests),
            };
            newState.ongoingRequests.delete(action.widget.id);
            newState.finishedRequests.set(action.widget.id, action.widgetDataResult);
            return newState;
        }
        case "invalidateDataForWidget": {
            const newState: WidgetDataState = {
                queuedRequests: new Set(state.queuedRequests),
                ongoingRequests: new Map(state.ongoingRequests),
                finishedRequests: new Map(state.finishedRequests),
            };
            newState.queuedRequests.delete(action.widgetId);

            const ongoingRequestDetails = newState.ongoingRequests.get(action.widgetId);
            if (ongoingRequestDetails !== undefined) {
                ongoingRequestDetails.abortController.abort();
                clearTimeout(ongoingRequestDetails.timeoutId);
                newState.ongoingRequests.delete(action.widgetId);
            }

            newState.finishedRequests.delete(action.widgetId);
            return newState;
        }
        case "invalidateDataForAllWidgets": {
            // console.log("widgetDataReducer: invalidateDataForAllWidgets");
            for (const ongoingRequestDetails of state.ongoingRequests.values()) {
                ongoingRequestDetails.abortController.abort();
                clearTimeout(ongoingRequestDetails.timeoutId);
            }
            const newState: WidgetDataState = {
                queuedRequests: new Set(),
                ongoingRequests: new Map(),
                finishedRequests: new Map(),
            };
            return newState;
        }
        case "cancelAllRequests": {
            return state;
        }
    }
}

export function generateInitialWidgetDataState(): WidgetDataState {
    const widgetDataState: WidgetDataState = {
        queuedRequests: new Set(),
        ongoingRequests: new Map(),
        finishedRequests: new Map(),
    };
    return widgetDataState;
}
